import React, { useState } from "react";
import {
  Alert, Box, Breadcrumbs, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Link as MLink, Stack, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography, FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Link } from "react-router-dom";
import {
  useGetTreatmentAreasQuery,
  useSaveTreatmentAreaMutation,
  useDeleteTreatmentAreaMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { PageSurface, LegacyTokens, TypeScale, B13, B13Ink, tint12 } from "../../../theme/b13Tokens";

/**
 * Batch 61 §2 — Treatment Areas settings page.
 *
 * Supervisor-managed list that replaces the fixed programs.category
 * enum. Seeded with only the categories actually present in real
 * data (see the migration). Empty by default on a fresh install so
 * the clinic builds their own vocabulary rather than inheriting
 * ours.
 */
interface TreatmentArea {
  id: number;
  name: string;
  description: string | null;
  color: string | null;
  order_number: number;
  is_active: number;
  isDelete: number;
  program_count: number;
}

export default function TreatmentAreasPage() {
  const { showMessage, showErrorMessage } = useNotifier();
  // Settings page manages every row — inactive ones must show so
  // admins can reactivate them. Pickers on other pages call this hook
  // with no args and get only the active set.
  const { data, isLoading, refetch } = useGetTreatmentAreasQuery({ include_inactive: true });
  const [saveArea, { isLoading: saving }] = useSaveTreatmentAreaMutation();
  const [deleteArea] = useDeleteTreatmentAreaMutation();

  const rows: TreatmentArea[] = Array.isArray(data?.data) ? (data!.data as TreatmentArea[]) : [];

  const [editing, setEditing] = useState<Partial<TreatmentArea> | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const openNew = () => setEditing({ name: "", description: "", color: "", order_number: rows.length, is_active: 1 });
  const openEdit = (row: TreatmentArea) => setEditing({ ...row });
  const close = () => setEditing(null);

  const handleSave = async () => {
    if (!editing || !editing.name?.trim()) {
      showErrorMessage("Name is required.");
      return;
    }
    try {
      const res: any = await saveArea({
        id: editing.id,
        name: editing.name.trim(),
        description: editing.description || null,
        color: editing.color || null,
        order_number: editing.order_number ?? 0,
        is_active: editing.is_active ?? 1,
      }).unwrap();
      if (res?.status) {
        showMessage(res.message || "Saved.");
        close();
        refetch();
      } else {
        showErrorMessage(res?.message || "Save failed.");
      }
    } catch {
      showErrorMessage("Save failed.");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res: any = await deleteArea(id).unwrap();
      if (res?.status) {
        showMessage(res.message || "Deleted.");
        setDeleteConfirmId(null);
        refetch();
      } else {
        showErrorMessage(res?.message || "Delete failed.");
        setDeleteConfirmId(null);
      }
    } catch {
      showErrorMessage("Delete failed.");
      setDeleteConfirmId(null);
    }
  };

  const deletingRow = rows.find((r) => r.id === deleteConfirmId);

  return (
    <Box sx={{ ...PageSurface, p: { xs: 2, md: 3 } }}>
      <Breadcrumbs sx={{ mb: 1.5, fontSize: 12 }} separator="/">
        <MLink underline="hover" component={Link} to="/aba-therapy/programs" sx={{ color: B13Ink.ink3 }}>ABA Therapy</MLink>
        <MLink underline="hover" component={Link} to="/aba-therapy/settings/treatment-areas" sx={{ color: B13Ink.ink3 }}>Settings</MLink>
        <Typography sx={{ fontSize: 12, color: LegacyTokens.ink }}>Treatment Areas</Typography>
      </Breadcrumbs>

      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography component="h1" sx={{ ...TypeScale.pageTitle, lineHeight: 1.15 }}>Treatment Areas</Typography>
          <Typography sx={{ ...TypeScale.caption, mt: 0.5, maxWidth: 720 }}>
            The clinical categories a program can belong to. Only areas your clinic has used are listed — add your own to build the taxonomy that fits your practice.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
          sx={{ backgroundColor: B13.primary, "&:hover": { backgroundColor: B13.primary, filter: "brightness(0.95)" } }}>
          Add Treatment Area
        </Button>
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: LegacyTokens.line }}>
        {isLoading ? (
          <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}><CircularProgress size={24} /></Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No Treatment Areas yet. Add the first one to make it selectable on new programs.
            </Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openNew}>Add first Treatment Area</Button>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TypeScale.tableHeader }}>Name</TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader }}>Description</TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader, width: 100 }}>Programs</TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader, width: 90 }}>Active</TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader, width: 100, textAlign: "right" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {r.color && <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: r.color, border: `1px solid ${LegacyTokens.line}` }} />}
                      <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{r.description || "—"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.program_count ?? 0}</Typography>
                  </TableCell>
                  <TableCell>
                    {/* Colour means state: active in the same blue as an
                       Active program status; inactive in muted ink3
                       (nothing happening here). */}
                    <Typography variant="caption" sx={{ color: r.is_active ? B13.primary : B13Ink.ink3 }}>
                      {r.is_active ? "Active" : "Inactive"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={r.program_count > 0 ? "In use by programs — reassign first" : "Delete"}>
                      <span>
                        <IconButton size="small" disabled={r.program_count > 0} onClick={() => setDeleteConfirmId(r.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Edit / Create dialog */}
      <Dialog open={!!editing} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle sx={{ color: "#274F70", fontWeight: 800 }}>
          {editing?.id ? "Edit Treatment Area" : "New Treatment Area"}
        </DialogTitle>
        <DialogContent dividers sx={{ px: "22px", py: "18px" }}>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              autoFocus fullWidth size="small" label="Name" required
              value={editing?.name ?? ""}
              onChange={(e) => setEditing((s) => ({ ...(s ?? {}), name: e.target.value }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            <TextField
              fullWidth size="small" multiline minRows={2} label="Description"
              value={editing?.description ?? ""}
              onChange={(e) => setEditing((s) => ({ ...(s ?? {}), description: e.target.value }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            {(() => {
              const isActive = !!(editing?.is_active ?? 1);
              return (
                // Two-column grid so Colour + Active are perfectly
                // co-heighted. Both children explicitly set height 52
                // via their own sx so the grid track can auto-fit
                // without stretching either one taller than intended.
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "180px minmax(0,1fr)" },
                    gap: "12px",
                    alignItems: "stretch",
                  }}
                >
                  {/* Colour — wrapped in a mini-card that mirrors the
                      Active card's radius/height/border. Native color
                      input kept; only the presentation container is
                      swapped so the two boxes align top + bottom. */}
                  <Box
                    sx={{
                      height: 52,
                      minHeight: 52,
                      borderRadius: "12px",
                      border: "1px solid #DCE5ED",
                      bgcolor: "#FFFFFF",
                      padding: "7px 12px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      transition: "border-color 180ms cubic-bezier(.4,0,.2,1)",
                      "&:hover": { borderColor: "#B7C6D6" },
                      "&:focus-within": { borderColor: "#5F86AC" },
                    }}
                  >
                    <Box
                      component="label"
                      htmlFor="ta-colour-input"
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#7A8C9D",
                        textTransform: "none",
                        letterSpacing: "0.02em",
                        marginBottom: "4px",
                        lineHeight: 1,
                      }}
                    >
                      Colour
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        component="input"
                        id="ta-colour-input"
                        type="color"
                        value={editing?.color || "#888888"}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditing((s) => ({ ...(s ?? {}), color: e.target.value }))
                        }
                        sx={{
                          appearance: "none",
                          WebkitAppearance: "none",
                          border: "1px solid #E1E8EF",
                          borderRadius: "6px",
                          width: 28,
                          height: 20,
                          padding: 0,
                          cursor: "pointer",
                          background: "none",
                          "&::-webkit-color-swatch-wrapper": { padding: 0 },
                          "&::-webkit-color-swatch": { border: "none", borderRadius: "4px" },
                          "&::-moz-color-swatch": { border: "none", borderRadius: "4px" },
                        }}
                      />
                      <Box
                        component="span"
                        sx={{
                          fontSize: 12,
                          fontFamily: "monospace",
                          color: "#50657A",
                          letterSpacing: "0.02em",
                        }}
                      >
                        {(editing?.color || "#888888").toUpperCase()}
                      </Box>
                    </Box>
                  </Box>

                  {/* Active toggle — polished mini-card. Bright blue
                      active state, soft neutral idle, secondary label
                      + status pill so the state is legible via three
                      signals (colour, label, pill). Local styling
                      only; no other Switch on the app is affected. */}
                  <Box
                    sx={{
                      height: 52,
                      minHeight: 52,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      padding: "6px 12px",
                      borderRadius: "12px",
                      border: `1px solid ${isActive ? "#BFDBFE" : "#DCE8F3"}`,
                      background: isActive
                        ? "linear-gradient(135deg, #EFF8FF 0%, #EAF4FF 100%)"
                        : "linear-gradient(135deg, #F8FBFF 0%, #F1F7FD 100%)",
                      transition: "all 180ms cubic-bezier(.4,0,.2,1)",
                    }}
                  >
                    <Switch
                      checked={isActive}
                      onChange={(e) => setEditing((s) => ({ ...(s ?? {}), is_active: e.target.checked ? 1 : 0 }))}
                      sx={{
                        width: 46,
                        height: 26,
                        padding: 0,
                        display: "flex",
                        "& .MuiSwitch-switchBase": {
                          padding: "3px",
                          transition: "all 180ms cubic-bezier(.4,0,.2,1)",
                          "&.Mui-checked": {
                            transform: "translateX(20px)",
                            color: "#FFFFFF",
                            "& + .MuiSwitch-track": {
                              background: "linear-gradient(90deg, #38BDF8 0%, #3B82F6 100%)",
                              borderColor: "#60A5FA",
                              opacity: 1,
                              boxShadow: "0 0 0 3px rgba(59,130,246,0.10)",
                            },
                            "& .MuiSwitch-thumb": {
                              backgroundColor: "#FFFFFF",
                              boxShadow: "0 3px 8px rgba(37,99,235,0.28)",
                            },
                            "&:hover .MuiSwitch-thumb": { transform: "scale(1.02)" },
                            "&.Mui-focusVisible .MuiSwitch-thumb": {
                              boxShadow: "0 0 0 3px rgba(59,130,246,0.22), 0 3px 8px rgba(37,99,235,0.28)",
                            },
                          },
                          "&:hover + .MuiSwitch-track": { backgroundColor: "#DFEAF4" },
                          "&.Mui-focusVisible .MuiSwitch-thumb": {
                            boxShadow: "0 0 0 3px rgba(59,130,246,0.22), 0 2px 6px rgba(23,50,77,0.14)",
                          },
                        },
                        "& .MuiSwitch-thumb": {
                          width: 20,
                          height: 20,
                          backgroundColor: "#FFFFFF",
                          boxShadow: "0 2px 6px rgba(23,50,77,0.14)",
                          transition: "all 180ms cubic-bezier(.4,0,.2,1)",
                        },
                        "& .MuiSwitch-track": {
                          borderRadius: 999,
                          border: "1px solid #D5E0EA",
                          backgroundColor: "#E8EEF5",
                          opacity: 1,
                          transition: "all 180ms cubic-bezier(.4,0,.2,1)",
                        },
                      }}
                      inputProps={{ "aria-label": "Active (selectable on new programs)" }}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: isActive ? "#2563EB" : "#50657A",
                            lineHeight: 1.2,
                          }}
                        >
                          Active
                        </Typography>
                        <Box
                          component="span"
                          sx={{
                            fontSize: 9,
                            fontWeight: 800,
                            letterSpacing: "0.06em",
                            padding: "2px 6px",
                            borderRadius: 999,
                            border: `1px solid ${isActive ? "#BFDBFE" : "#DCE5ED"}`,
                            bgcolor: isActive ? "#EAF4FF" : "#F4F7FA",
                            color: isActive ? "#2563EB" : "#74879A",
                            lineHeight: 1.4,
                          }}
                        >
                          {isActive ? "ACTIVE" : "INACTIVE"}
                        </Box>
                      </Stack>
                      <Typography
                        component="span"
                        sx={{ fontSize: 12, fontWeight: 500, color: "#8495A6", lineHeight: 1.35, mt: 0.25 }}
                      >
                        selectable on new programs
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={close}
            sx={{
              textTransform: "none",
              color: "#31577F",
              fontWeight: 600,
              "&:hover": { bgcolor: "#F2F7FB" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !editing?.name?.trim()}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "#FFFFFF",
              background: "linear-gradient(135deg, #3B82F6, #2563EB)",
              boxShadow: "0 5px 14px rgba(37,99,235,0.22)",
              "&:hover": {
                background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                boxShadow: "0 6px 16px rgba(37,99,235,0.28)",
              },
              "&.Mui-disabled": {
                background: "#E6ECF3",
                color: "#9AAEC2",
                boxShadow: "none",
              },
            }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Treatment Area?</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {deletingRow ? `"${deletingRow.name}" will be soft-deleted and hidden from new programs.` : ""}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
